import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import { Formik, Form, Field } from 'formik';
import { Copy, AlertTriangle, Terminal, Trash2, Clock, Shield, Key, ChevronRight, Loader2, Zap, ArrowRight } from 'lucide-react';
import { projectsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Button from '../../components/ui/Button';
import { motion } from 'framer-motion';
import Modal from '../ui/Modal';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const TokenSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(3, "Token name must be at least 3 characters")
    .max(50, "Token name too long")
    .required("Token name is required"),

  environment: Yup.string()
    .oneOf(["all", "dev", "prod"], "Invalid scope")
    .required("Environment is required"),
});

export default function ServiceTokens({ projectId }) {
  const [generatedToken, setGeneratedToken] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenToRemove, setTokenToRemove] = useState(null);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const { usage } = useAuth()
  const navigate = useNavigate()

  const fetchTokens = async () => {
    try {
      const data = await projectsAPI.getTokens(projectId);
      setTokens(data);
    } catch (error) {
      console.error("Failed to load tokens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, [projectId]);

  const openRemoveModal = (token) => {
    setTokenToRemove(token);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await projectsAPI.deleteToken(projectId, tokenToRemove);
      fetchTokens();
    } catch (error) {
      console.log("Failed to remove token", error);
    } finally {
      setConfirmOpen(false);
      setTokenToRemove(null);
    }
  };

  return (
    <div className="space-y-10">

      <h3 className="text-xl md:text-2xl font-black text-white tracking-tighter">Service Tokens</h3>
      {/* --- Section 1: Create New Token --- */}
      <div className="space-y-4">
        <div className="flex items-start gap-4 p-5 bg-orange-500/[0.03] border border-orange-500/10 rounded-2xl">
          <Terminal className="text-orange-500 shrink-0 mt-1" size={20} />
          <div>
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-500">Node Authentication</h4>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Generate machine-level tokens for CI/CD pipelines. These allow programmatic access to your vault without user interaction.
            </p>
            <p></p>
            <Link to="/dashboard/docs" className="text-xs text-gray-500 mt-1 leading-relaxed underline text-orange-500">
              Visit Documentation for API and Endpoints(5)
            </Link>
          </div>
        </div>

        {!generatedToken && (
          <Formik
            initialValues={{ name: '', environment: 'all' }}
            validationSchema={TokenSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              console.log("Submitting:", values); // DEBUG LOG
              try {
                const data = await projectsAPI.createToken(projectId, values.name, values.environment);
                setGeneratedToken(data.token);
                fetchTokens();
                resetForm();
                toast.success("Token Generated");
              } catch (err) {
                console.error(err);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {/* Added errors and touched here to display validation issues */}
            {({ isSubmitting, errors, touched }) => (
              <Form className="flex flex-col lg:flex-row gap-4 items-start md:items-start bg-white/[0.01] border border-white/5 p-6 rounded-3xl">

                <div className="w-full flex-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-700 mb-2 block ml-1">Token Name</label>
                  <Field
                    name="name"
                    placeholder="e.g. CI-PRODUCTION-KEY"
                    className={`w-full bg-black/40 border rounded-xl px-4 py-3 text-xs text-white outline-none transition-all placeholder:text-gray-800
                      ${errors.name && touched.name ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-orange-500'}
                    `}
                  />
                  {/* VISUAL ERROR FEEDBACK */}
                  {errors.name && touched.name && (
                    <div className="text-red-500 text-[10px] font-bold mt-1.5 flex items-center gap-1">
                      <AlertTriangle size={10} /> {errors.name}
                    </div>
                  )}
                </div>
                <div className='flex gap-4 flex-col md:flex-row'>
                  {/* <div className="w-full md:w-40">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-700 mb-2 block ml-1">Scope</label>
                    <div className="relative">
                      <Field as="select" name="environment" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-xs text-white focus:border-orange-500 outline-none appearance-none cursor-pointer">
                        <option value="all">All Clusters</option>
                        <option value="dev">Dev Only</option>
                        <option value="prod">Prod Only</option>
                      </Field>
                      <ChevronRight size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-700 rotate-90 pointer-events-none" />
                    </div>
                  </div> */}

                  <div className="pt-6 w-full md:w-auto">

                    {usage?.limits?.tokensPerProject > tokens.length ? <Button disabled={isSubmitting} type="submit" variant="primary" className="w-full md:w-auto h-[42px] px-8">
                      {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : "Generate"}
                    </Button>
                      :

                      <div onClick={() => navigate('/dashboard/subscription')} className="flex items-center justify-between lg:justify-start gap-3 px-4 py-2 bg-[#0d0d0d] border border-orange-500/20 rounded-xl group transition-all hover:border-orange-500/40 w-full lg:w-auto">

                        <div className="flex items-center gap-3">
                          <div className="flex shrink-0 w-6 h-6 rounded-lg bg-orange-500/10 items-center justify-center">
                            <Zap size={13} className="text-orange-500 animate-pulse" />
                          </div>

                          <div className="flex flex-col lg:flex-row lg:items-center gap-0 lg:gap-3">
                            <p className="text-[10px] lg:text-[11px] font-bold text-orange-500 hover:text-orange-400 transition-colors uppercase tracking-widest leading-none">
                              Limit Reached
                            </p>
                          </div>
                        </div>

                        {/* Mobile-only Arrow (Keeps it compact) */}
                        <ArrowRight size={14} className="text-orange-500 lg:hidden" />
                      </div>}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {/* ... (Rest of your generatedToken success view and list view remains the same) ... */}
        {generatedToken && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-6 md:p-8 bg-emerald-500/[0.02] border border-emerald-500/20 rounded-[2rem] relative overflow-hidden"
          >
            <div className="flex items-center gap-3 text-emerald-500 mb-4">
              <Shield size={20} />
              <span className="text-xs font-black uppercase tracking-[0.2em]">Token Successfully Initialized</span>
            </div>

            <div className="bg-black/60 border border-emerald-500/20 p-5 rounded-2xl flex flex-col md:flex-row items-center gap-4">
              <code className="text-[11px] md:text-sm text-emerald-400 font-mono break-all flex-1 text-center md:text-left">
                {generatedToken}
              </code>
              <button
                onClick={() => { navigator.clipboard.writeText(generatedToken); toast.success("Token Copied!"); }}
                className="flex items-center gap-2 bg-emerald-500 text-black px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-400 transition-colors w-full md:w-auto justify-center"
              >
                <Copy size={14} /> Copy
              </button>
            </div>

            <div className="mt-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-red-500/60">
                <AlertTriangle size={14} />
                <p className="text-[9px] font-black uppercase tracking-widest">This token will never be shown again.</p>
              </div>
              <button onClick={() => setGeneratedToken(null)} className="text-[9px] font-black text-gray-500 hover:text-white uppercase tracking-widest underline underline-offset-4 decoration-gray-800 transition-all">
                I have secured this key
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* --- Section 2: Active Tokens List --- */}
      <div className="pt-10 border-t border-white/5">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-black text-white uppercase tracking-widest">Authorized Nodes</h3>
          <span className="text-[10px] font-black text-gray-700 uppercase tracking-widest">{tokens.length} Active</span>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {tokens.map(token => (
            <div key={token._id} className="group flex flex-col md:flex-row md:items-center justify-between p-5 bg-white/[0.02] border border-white/5 rounded-2xl hover:border-white/10 transition-all">
              <div className="flex items-center gap-5">
                <div className="w-10 h-10 rounded-xl bg-[#0a0a0a] border border-white/5 flex items-center justify-center text-gray-700 group-hover:text-orange-500 transition-colors">
                  <Key size={18} />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <p className="font-black text-white text-sm tracking-tight">{token.name}</p>
                    <span className="text-[8px] bg-white/5 border border-white/10 px-2 py-0.5 rounded-full uppercase font-black tracking-tighter text-gray-500">
                      {token.environment === 'all' ? 'global' : token.environment}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-gray-600 font-bold mt-1">
                    <span className="font-mono text-gray-700">{token.tokenPrefix}••••••••••••••••</span>
                    <span className="flex items-center gap-1.5 uppercase tracking-widest text-[9px]">
                      <Clock size={12} className="opacity-50" />
                      {token.lastUsed ? `Last Activity: ${new Date(token.lastUsed).toLocaleDateString()}` : 'Idle State'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end mt-4 md:mt-0 pt-4 md:pt-0 border-t md:border-t-0 border-white/5">
                <button
                  onClick={() => openRemoveModal(token._id)}
                  className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-700 hover:text-red-500 hover:bg-red-500/5 rounded-xl transition-all"
                >
                  <Trash2 size={14} />
                  <span className="md:hidden">Revoke Authorization</span>
                </button>
              </div>
            </div>
          ))}

          {!loading && tokens.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 bg-white/[0.01] border border-dashed border-white/5 rounded-[2rem] text-gray-700">
              <Shield size={32} className="mb-3 opacity-20" />
              <p className="text-[10px] font-black uppercase tracking-widest">No service tokens provisioned.</p>
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-orange-500" size={24} />
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isConfirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Revoke Token"
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-400">
            Are you sure you want to remove this token?
          </p>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirmDelete}
            >
              Remove Token
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}